import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconArrowLeft, IconCheck, IconPlus, IconTrash } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function NuevoCaso() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", age: 8, grade: "", condition: "",
    country: "Honduras", city: "",
  });
  const [parents, setParents] = useState([
    { role: "madre", name: "", email: "", phone: "" },
  ]);
  const [custom, setCustom] = useState([]); // [{k,v}]
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setParent = (i, k, v) => setParents(parents.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const custom_fields = Object.fromEntries(custom.filter((c) => c.k).map((c) => [c.k, c.v]));
      const cleanParents = parents
        .filter((p) => p.name)
        .map((p) => ({
          role: p.role,
          name: p.name,
          email: p.email && p.email.trim() ? p.email.trim() : null,
          phone: p.phone && p.phone.trim() ? p.phone.trim() : null,
        }));
      const body = {
        ...form,
        age: Number(form.age),
        parents: cleanParents,
        custom_fields,
      };
      const { data } = await api.post("/circulo/children", body);
      nav(`/app/circulo/child/${data.id}`);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app/circulo")} data-testid="nc-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Casos" : "Cases"}
        </button>
        <span className="t">{lang === "es" ? "NUEVO CASO" : "NEW CASE"}</span>
      </div>
      <form className="wrap-app py-10 max-w-[720px]" onSubmit={submit}>
        <p className="eyebrow">{lang === "es" ? "Alta de caso" : "Case intake"}</p>
        <h1 className="font-display font-medium text-[26px] mt-1 mb-6">
          {lang === "es" ? "Registrar un niño" : "Register a child"}
        </h1>

        <div className="field">
          <label>{lang === "es" ? "Nombre del niño" : "Child name"}</label>
          <input className="input" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="nc-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field"><label>{lang === "es" ? "Edad" : "Age"}</label><input type="number" min="3" max="20" className="input" value={form.age} onChange={(e) => set("age", e.target.value)} data-testid="nc-age" /></div>
          <div className="field"><label>{lang === "es" ? "Grado" : "Grade"}</label><input className="input" value={form.grade} onChange={(e) => set("grade", e.target.value)} data-testid="nc-grade" /></div>
        </div>
        <div className="field"><label>{lang === "es" ? "Condición o diagnóstico" : "Condition"}</label><input className="input" placeholder="TDAH, TEA, dislexia..." value={form.condition} onChange={(e) => set("condition", e.target.value)} data-testid="nc-cond" /></div>

        {parents.map((p, i) => (
          <div key={i} className="card mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="lbl m-0">{p.role}</span>
              {parents.length > 1 && (
                <button type="button" className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => setParents(parents.filter((_, idx) => idx !== i))}>
                  <IconTrash size={12} />
                </button>
              )}
            </div>
            <div className="field"><label>{lang === "es" ? "Nombre" : "Name"}</label><input className="input" value={p.name} onChange={(e) => setParent(i, "name", e.target.value)} data-testid={`nc-parent-name-${i}`} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="field"><label>{lang === "es" ? "Correo" : "Email"}</label><input className="input" type="email" value={p.email} onChange={(e) => setParent(i, "email", e.target.value)} data-testid={`nc-parent-email-${i}`} /></div>
              <div className="field"><label>{lang === "es" ? "Teléfono" : "Phone"}</label><input className="input" value={p.phone} onChange={(e) => setParent(i, "phone", e.target.value)} data-testid={`nc-parent-phone-${i}`} /></div>
            </div>
            <div className="field"><label>Rol</label>
              <select className="select-css" value={p.role} onChange={(e) => setParent(i, "role", e.target.value)}>
                <option value="madre">Madre</option><option value="padre">Padre</option><option value="tutor">Tutor</option>
              </select>
            </div>
          </div>
        ))}
        <button type="button" className="btn-subt mt-2" onClick={() => setParents([...parents, { role: "padre", name: "", email: "", phone: "" }])} data-testid="nc-add-parent">
          <IconPlus size={12} /> {lang === "es" ? "Agregar otro" : "Add another"}
        </button>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div className="field"><label>{lang === "es" ? "País" : "Country"}</label><input className="input" value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
          <div className="field"><label>{lang === "es" ? "Ciudad" : "City"}</label><input className="input" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
        </div>

        <div className="card mt-2">
          <div className="flex items-center justify-between">
            <p className="lbl m-0">{lang === "es" ? "Variables propias" : "Custom fields"}</p>
            <button type="button" className="btn-subt" onClick={() => setCustom([...custom, { k: "", v: "" }])}>
              <IconPlus size={12} /> {lang === "es" ? "Agregar" : "Add"}
            </button>
          </div>
          <p className="text-tert text-[12px] mt-1">
            {lang === "es" ? "Ej. escuela, alergias, terapias." : "e.g. school, allergies, therapies."}
          </p>
          {custom.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-2 mt-2">
              <input className="input" placeholder="clave" value={c.k} onChange={(e) => setCustom(custom.map((x, idx) => idx === i ? { ...x, k: e.target.value } : x))} />
              <input className="input" placeholder="valor" value={c.v} onChange={(e) => setCustom(custom.map((x, idx) => idx === i ? { ...x, v: e.target.value } : x))} />
              <button type="button" className="btn-ghost btn text-[11px]" onClick={() => setCustom(custom.filter((_, idx) => idx !== i))}><IconTrash size={12} /></button>
            </div>
          ))}
        </div>

        {error && <p className="text-errtext text-[13px] mt-3">{error}</p>}
        <button type="submit" className="btn mt-6" disabled={saving} data-testid="nc-submit">
          <IconCheck size={13} /> {saving ? "..." : (lang === "es" ? "Crear caso" : "Create case")}
        </button>
      </form>
    </>
  );
}
